import { AddIcon } from '@chakra-ui/icons';
import {
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CompanyUsersTab,
  CurrentUsageTab,
  ExportUsersButton,
  UsageHistoryDetailTab,
  UsageHistoryTab,
} from 'components/CompanyDetail';
import { getUserInfoStorage } from 'helpers/lib';
import {
  useOrganizationUsageCurrent,
  useOrganizationUsageHistoryCurrent,
} from 'queries/useOrganization';
import { useListUsers, useUpdateUser } from 'queries/useUser';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UserProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const User: React.FC<UserProps> = ({ isOpen = true, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const queryClient = useQueryClient();
  const _toast = useToast();

  // Get users using React Query
  const { data: users, isLoading: isLoadingUsers } = useListUsers();

  const bgModal = useColorModeValue('white', 'gray.800');
  const colorText = useColorModeValue('gray.800', 'white');

  // Handle close modal and clear cache
  const handleClose = () => {
    // Invalidate cache for current organization data
    queryClient.invalidateQueries({
      queryKey: ['organizations', 'usageCurrent'],
    });
    queryClient.invalidateQueries({
      queryKey: ['organizations', 'usageHistoryCurrent'],
    });
    queryClient.invalidateQueries({
      queryKey: ['organizations', 'list'],
    });
    // Invalidate users list cache
    queryClient.invalidateQueries({
      queryKey: ['users'],
    });
    if (onClose) {
      onClose();
    }
  };

  // Handle user update using React Query
  const updateUserMutation = useUpdateUser();
  const handleUpdateUser = async (userId: string, userData: any) => {
    console.log('CompanyDetailModal.handleUpdateUser called with:', { userId, userData });
    try {
      // Find the current user to get the email
      const currentUser = users?.find((user: any) => user.id === userId);
      if (!currentUser) {
        throw new Error('User not found');
      }

      const result = await updateUserMutation.mutateAsync({
        userId,
        userData: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          email: currentUser.email, // Include email from current user data
        },
      });

      console.log('CompanyDetailModal.handleUpdateUser response:', result);

      // Check if result is valid (has required properties)
      if (!result || !result.id) {
        console.error('CompanyDetailModal.handleUpdateUser failed: Invalid response');
        throw new Error('User update failed: Invalid response');
      }

      console.log('CompanyDetailModal.handleUpdateUser successful:', result);
      // Success toast will be handled in CompanyUsersTab after successful API call
    } catch (error) {
      console.error('CompanyDetailModal.handleUpdateUser failed:', error);
      // Re-throw the error so it can be caught by CompanyUsersTab
      throw error;
    }
  };

  // Fetch usage data
  const {
    data: usageData,
    isLoading: isLoadingUsage,
    error: _usageError,
  } = useOrganizationUsageCurrent();
  const {
    data: usageHistoryData,
    isLoading: isLoadingHistory,
    error: _historyError,
  } = useOrganizationUsageHistoryCurrent();

  // Get company name from usageData or user info
  const getCompanyName = () => {
    // First try to get from usageData if it has organization info
    if (usageData?.name) {
      return usageData.name;
    }
    // Fallback to user's organization from token
    const userInfo = getUserInfoStorage();
    return userInfo?.tokenParsed?.org || '';
  };

  const companyName = getCompanyName();

  // Show loading state
  if (isLoadingUsage || isLoadingHistory || isLoadingUsers) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size='6xl'>
        <ModalOverlay />
        <ModalContent bg={bgModal}>
          <ModalHeader pl={4} color={colorText}>
            {t('route.companyManagement')} {companyName && `- ${companyName}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Center py={20}>
              <Spinner color='blue.500' size='xl' />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose || (() => {})} size='6xl'>
      <ModalOverlay />
      <ModalContent maxH='90vh' bg={bgModal}>
        <ModalHeader pl={4} color={colorText}>
          {t('route.companyManagement')} {companyName && `- ${companyName}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH='calc(90vh - 120px)' p={0}>
          <Tabs
            h='100%'
            colorScheme='blue'
            index={activeTab}
            onChange={setActiveTab}
            variant='enclosed'
          >
            <TabList>
              <Tab>{t('managementSystem.companyDetail.currentUsage')}</Tab>
              <Tab>{t('managementSystem.companyDetail.usageHistory')}</Tab>
              <Tab>{t('managementSystem.companyDetail.usageHistoryDetail')}</Tab>
              <Tab>{t('managementSystem.companyDetail.companyUsers')}</Tab>
            </TabList>

            {/* Add User Button - Always visible when on Company Users tab */}
            {activeTab === 3 && (
              <Flex justify='flex-end' gap={2} px={4} pt={2}>
                <ExportUsersButton isDisabled={isEditingUser} />
                <Button
                  colorScheme='blue'
                  isDisabled={isEditingUser}
                  leftIcon={<AddIcon />}
                  onClick={() => {
                    const event = new CustomEvent('triggerAddUser');
                    window.dispatchEvent(event);
                  }}
                  size='sm'
                >
                  {t('managementSystem.companyDetail.addUser')}
                </Button>
              </Flex>
            )}

            <TabPanels overflow='auto' h='calc(100% - 60px)'>
              {/* Current Usage Tab */}
              <TabPanel h='100%' p={4}>
                <CurrentUsageTab
                  usageData={usageData}
                  limitation={usageData?.limitation}
                  showTokenUsage={false}
                />
              </TabPanel>

              {/* Usage History Tab */}
              <TabPanel h='100%' p={4}>
                <UsageHistoryTab usageData={usageHistoryData} />
              </TabPanel>

              {/* Usage History Detail Tab */}
              <TabPanel h='100%' p={4}>
                <UsageHistoryDetailTab usageData={usageHistoryData} />
              </TabPanel>

              {/* Company Users Tab */}
              <TabPanel h='100%' p={4}>
                <CompanyUsersTab
                  users={users}
                  organizationId=''
                  onUpdateUser={handleUpdateUser}
                  onEditingStateChange={setIsEditingUser}
                  isNotSuperAdmin={true}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default User;
